import React, { FC, useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { userApi, CreateUserRequest, UpdateUserRequest } from '@/api/users';
import { tenantApi, Tenant } from '@/api/tenants';

const FormContainer = styled.div`
  min-height: 100vh;
  background: var(--gray-50);
  padding: 2rem;
`;

const FormContent = styled.div`
  max-width: 1560px;
  margin: 0 auto;
`;

const Header = styled.header`
  margin-bottom: 2rem;
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: white;
  border: 1px solid var(--gray-300);
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--gray-700);
  cursor: pointer;
  transition: all 0.2s;
  margin-bottom: 1rem;

  svg {
    width: 1.25rem;
    height: 1.25rem;
  }

  &:hover {
    border-color: var(--primary-600);
    color: var(--primary-600);
  }
`;

const Title = styled.h1`
  font-size: 1.875rem;
  font-weight: 700;
  color: var(--gray-900);
  margin-bottom: 0.5rem;
`;

const Subtitle = styled.p`
  font-size: 0.875rem;
  color: var(--gray-600);
`;

const Form = styled.form`
  background: white;
  border-radius: 0.75rem;
  box-shadow: var(--shadow-sm);
  padding: 2rem;
`;

const Section = styled.div`
  margin-bottom: 2rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h2`
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--gray-900);
  margin-bottom: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid var(--gray-200);
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--gray-700);

  .required {
    color: var(--error-600);
    margin-left: 0.25rem;
  }
`;

const Input = styled.input`
  padding: 0.625rem 0.875rem;
  border: 1px solid var(--gray-300);
  border-radius: 0.5rem;
  font-size: 0.875rem;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: var(--primary-600);
    box-shadow: 0 0 0 3px rgba(234, 88, 12, 0.1);
  }

  &:disabled {
    background: var(--gray-100);
    cursor: not-allowed;
  }
`;

const Select = styled.select`
  padding: 0.625rem 0.875rem;
  border: 1px solid var(--gray-300);
  border-radius: 0.5rem;
  font-size: 0.875rem;
  transition: all 0.2s;
  background: white;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: var(--primary-600);
    box-shadow: 0 0 0 3px rgba(234, 88, 12, 0.1);
  }

  &:disabled {
    background: var(--gray-100);
    cursor: not-allowed;
  }
`;

const HelpText = styled.p`
  font-size: 0.75rem;
  color: var(--gray-500);
  margin-top: 0.25rem;
`;

const CheckboxGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 0;

  input[type='checkbox'] {
    width: 1rem;
    height: 1rem;
    cursor: pointer;
    accent-color: var(--primary-600);
  }

  label {
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--gray-700);
    cursor: pointer;
    margin: 0;
  }
`;

const RoleCheckboxes = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const FormActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid var(--gray-200);
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  padding: 0.625rem 1.5rem;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: none;

  ${(props) =>
    props.$variant === 'primary'
      ? `
    background: var(--primary-600);
    color: white;
    
    &:hover:not(:disabled) {
      background: var(--primary-700);
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(234, 88, 12, 0.3);
    }
  `
      : `
    background: white;
    color: var(--gray-700);
    border: 1px solid var(--gray-300);
    
    &:hover:not(:disabled) {
      border-color: var(--gray-400);
      background: var(--gray-50);
    }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4rem;

  &::after {
    content: '';
    width: 2rem;
    height: 2rem;
    border: 3px solid var(--gray-200);
    border-top-color: var(--primary-600);
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const ErrorMessage = styled.div`
  padding: 1rem;
  background: var(--error-50);
  border: 1px solid var(--error-200);
  border-radius: 0.5rem;
  color: var(--error-700);
  font-size: 0.875rem;
  margin-bottom: 1.5rem;
`;

const UserForm: FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = !!id;

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tenants, setTenants] = useState<Tenant[]>([]);

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    tenant_id: '',
    roles: [] as string[],
    is_active: true,
    is_verified: false,
  });

  // Load user data for editing
  useEffect(() => {
    if (isEditMode && id) {
      loadUser(id);
    }
    loadTenants();
  }, [id, isEditMode]);

  const loadUser = async (userId: string) => {
    setLoading(true);
    setError(null);
    try {
      const user = await userApi.getUser(userId);
      setFormData({
        email: user.email,
        password: '',
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        tenant_id: user.tenant_id || '',
        roles: user.roles || [],
        is_active: user.is_active,
        is_verified: user.is_verified,
      });
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load user');
    } finally {
      setLoading(false);
    }
  };

  const loadTenants = async () => {
    try {
      const response = await tenantApi.getTenants({ per_page: 100 });
      setTenants(response.tenants || []);
    } catch (err) {
      // Ignore tenant loading errors
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.email.trim()) {
      setError('Email is required');
      return;
    }

    if (!isEditMode && !formData.password) {
      setError('Password is required');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      if (isEditMode && id) {
        const updateData: UpdateUserRequest = {
          email: formData.email,
          first_name: formData.first_name || undefined,
          last_name: formData.last_name || undefined,
          tenant_id: formData.tenant_id || undefined,
          roles: formData.roles,
          is_active: formData.is_active,
          is_verified: formData.is_verified,
        };
        if (formData.password) {
          updateData.password = formData.password;
        }
        await userApi.updateUser(id, updateData);
      } else {
        const createData: CreateUserRequest = {
          email: formData.email,
          password: formData.password,
          first_name: formData.first_name || undefined,
          last_name: formData.last_name || undefined,
          tenant_id: formData.tenant_id || undefined,
          roles: formData.roles.length > 0 ? formData.roles : ['ROLE_USER'],
          is_active: formData.is_active,
          is_verified: formData.is_verified,
        };
        await userApi.createUser(createData);
      }

      navigate('/users');
    } catch (err: any) {
      setError(
        err.response?.data?.error ||
          `Failed to ${isEditMode ? 'update' : 'create'} user`
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleRoleToggle = (role: string) => {
    setFormData((prev) => ({
      ...prev,
      roles: prev.roles.includes(role)
        ? prev.roles.filter((r) => r !== role)
        : [...prev.roles, role],
    }));
  };

  if (loading) {
    return (
      <FormContainer>
        <FormContent>
          <LoadingSpinner />
        </FormContent>
      </FormContainer>
    );
  }

  return (
    <FormContainer>
      <FormContent>
        <Header>
          <BackButton onClick={() => navigate('/users')}>
            <ArrowLeftIcon />
            Back to Users
          </BackButton>
          <Title>{isEditMode ? 'Edit User' : 'Create New User'}</Title>
          <Subtitle>
            {isEditMode
              ? 'Update user information and permissions'
              : 'Add a new user to the system'}
          </Subtitle>
        </Header>

        <Form onSubmit={handleSubmit}>
          {error && <ErrorMessage>{error}</ErrorMessage>}

          <Section>
            <SectionTitle>Basic Information</SectionTitle>
            <FormGrid>
              <FormGroup>
                <Label>
                  Email<span className="required">*</span>
                </Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  placeholder="user@example.com"
                  required
                />
                <HelpText>User's email address (used for login)</HelpText>
              </FormGroup>

              <FormGroup>
                <Label>
                  Password{!isEditMode && <span className="required">*</span>}
                </Label>
                <Input
                  type="password"
                  value={formData.password}
                  onChange={(e) => handleChange('password', e.target.value)}
                  placeholder={
                    isEditMode
                      ? 'Leave blank to keep current'
                      : 'Enter password'
                  }
                  required={!isEditMode}
                />
                <HelpText>
                  {isEditMode
                    ? 'Leave blank to keep current password'
                    : 'Minimum 8 characters'}
                </HelpText>
              </FormGroup>

              <FormGroup>
                <Label>First Name</Label>
                <Input
                  type="text"
                  value={formData.first_name}
                  onChange={(e) => handleChange('first_name', e.target.value)}
                  placeholder="John"
                />
              </FormGroup>

              <FormGroup>
                <Label>Last Name</Label>
                <Input
                  type="text"
                  value={formData.last_name}
                  onChange={(e) => handleChange('last_name', e.target.value)}
                  placeholder="Doe"
                />
              </FormGroup>

              <FormGroup>
                <Label>Tenant</Label>
                <Select
                  value={formData.tenant_id}
                  onChange={(e) => handleChange('tenant_id', e.target.value)}
                >
                  <option value="">No Tenant</option>
                  {tenants.map((tenant) => (
                    <option key={tenant.id} value={tenant.id}>
                      {tenant.company_name} ({tenant.subdomain})
                    </option>
                  ))}
                </Select>
                <HelpText>Assign user to a specific tenant</HelpText>
              </FormGroup>
            </FormGrid>
          </Section>

          <Section>
            <SectionTitle>Roles & Permissions</SectionTitle>
            <RoleCheckboxes>
              <CheckboxGroup>
                <input
                  type="checkbox"
                  id="role_user"
                  checked={formData.roles.includes('ROLE_USER')}
                  onChange={() => handleRoleToggle('ROLE_USER')}
                />
                <label htmlFor="role_user">User - Basic access</label>
              </CheckboxGroup>

              <CheckboxGroup>
                <input
                  type="checkbox"
                  id="role_admin"
                  checked={formData.roles.includes('ROLE_ADMIN')}
                  onChange={() => handleRoleToggle('ROLE_ADMIN')}
                />
                <label htmlFor="role_admin">Admin - Full system access</label>
              </CheckboxGroup>

              <CheckboxGroup>
                <input
                  type="checkbox"
                  id="role_support"
                  checked={formData.roles.includes('ROLE_SUPPORT')}
                  onChange={() => handleRoleToggle('ROLE_SUPPORT')}
                />
                <label htmlFor="role_support">
                  Support - Customer support access
                </label>
              </CheckboxGroup>
            </RoleCheckboxes>
          </Section>

          <Section>
            <SectionTitle>Status</SectionTitle>
            <CheckboxGroup>
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => handleChange('is_active', e.target.checked)}
              />
              <label htmlFor="is_active">User is active</label>
            </CheckboxGroup>
            <HelpText style={{ marginTop: '0.5rem' }}>
              Inactive users cannot log in
            </HelpText>

            <CheckboxGroup style={{ marginTop: '1rem' }}>
              <input
                type="checkbox"
                id="is_verified"
                checked={formData.is_verified}
                onChange={(e) => handleChange('is_verified', e.target.checked)}
              />
              <label htmlFor="is_verified">Email is verified</label>
            </CheckboxGroup>
            <HelpText style={{ marginTop: '0.5rem' }}>
              Verified users have confirmed their email address
            </HelpText>
          </Section>

          <FormActions>
            <Button
              type="button"
              $variant="secondary"
              onClick={() => navigate('/users')}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button type="submit" $variant="primary" disabled={submitting}>
              {submitting
                ? 'Saving...'
                : isEditMode
                  ? 'Update User'
                  : 'Create User'}
            </Button>
          </FormActions>
        </Form>
      </FormContent>
    </FormContainer>
  );
};

export default UserForm;
